import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Button, Form, OverlayTrigger, Spinner, Table, Tooltip,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import dayjs from 'dayjs';
import PartnersService from '../../Services/PartnersService';
import PricingService from '../../Services/PricingService';

/**
 * Editable grid + commit results for the offer→bookings workflow.
 *
 * Controlled component: parent (IngestWorkflow) owns the `rows` array
 * and reacts to `onRowEdit(idx, patch)` callbacks. Parent also owns
 * the "Ingest all" submission — see `onIngestAll` prop.
 *
 * Inline-edited fields (per Code-Quality decision: explicit minimum,
 * no full Zod re-import for client-side gating; server validates and
 * the operator sees row-level errors after submit):
 *   - full_name
 *   - date_datetime (Budapest-local datetime-local input → ISO UTC)
 *   - passengers.adults
 *   - price + currency
 *   - partner_id
 *   - category.name
 *
 * Per-row commit outcomes (when `commitResult` is supplied):
 *   - ok + !deduplicated   → green "Created" badge with booking_reference
 *   - ok + deduplicated    → grey "Already ingested" badge
 *   - !ok                  → red "Failed" badge with the row error tooltip
 */

const NUMERIC_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

// Render the ISO UTC datetime as a Budapest-local string that fits the
// `<input type="datetime-local">` element's expected format. Returns
// '' when the value is missing/unparseable so the input renders empty
// rather than 'Invalid Date'.
//
// Uses Intl directly instead of dayjs.format() because the latter
// renders in the host browser's timezone — which is fine in
// production (operator's browser is Hungary) but flaky in tests and
// for operators travelling abroad. Intl lets us pin to
// Europe/Budapest regardless of host TZ.
function isoToBudapestLocalInput(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Budapest',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  // Some hosts emit '24' for midnight under hour12=false; normalize so
  // the input element accepts it.
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
}

// datetime-local input → ISO UTC. The operator enters the value as
// Budapest-local (operators don't think in UTC); we re-interpret the
// broken-down parts as Europe/Budapest regardless of the browser
// timezone, then return UTC ISO for the backend. Returns null on bad
// input so the controlled cell flags it immediately.
function budapestLocalInputToIso(value) {
  if (!value) return null;
  const m = dayjs(value);
  if (!m.isValid()) return null;
  // 1) Build a UTC instant from the parts (i.e. treat the parts AS IF
  //    they were UTC — wrong by exactly Budapest's offset for that
  //    moment).
  const parts = m.toDate();
  const browserGuess = new Date(Date.UTC(
    parts.getFullYear(),
    parts.getMonth(),
    parts.getDate(),
    parts.getHours(),
    parts.getMinutes(),
  ));
  // 2) Find Europe/Budapest's offset at that instant via Intl —
  //    handles CET/CEST DST automatically.
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Budapest', hour: '2-digit', hour12: false,
  });
  const budapestHour = parseInt(fmt.format(browserGuess), 10);
  const utcHour = browserGuess.getUTCHours();
  const budapestOffsetMin = ((budapestHour - utcHour + 24) % 24) * 60;
  // 3) Subtract Budapest's offset to get the true UTC instant the
  //    operator meant when they typed the local time.
  const utcInstant = new Date(browserGuess.getTime() - budapestOffsetMin * 60 * 1000);
  return utcInstant.toISOString();
}

function formatPickupDisplay(raw) {
  if (!raw) return '—';
  const m = dayjs(raw);
  if (!m.isValid()) return raw;
  return m.format('YYYY-MM-DD HH:mm');
}

function formatPassengers(passengers) {
  if (!passengers || typeof passengers !== 'object') return '—';
  const { children } = passengers;
  const adults = passengers.adults ?? passengers.amount ?? '—';
  if (children) return `${adults} + ${children}`;
  return String(adults);
}

function formatRoute(row) {
  const from = row.destination_from?.address || '—';
  const to = row.destination_to?.address || '—';
  return `${from} → ${to}`;
}

function ResultBadge({ outcome }) {
  if (!outcome) return null;
  if (!outcome.ok) {
    return (
      <OverlayTrigger overlay={<Tooltip>{outcome.error || 'Failed'}</Tooltip>}>
        <Badge bg="danger">
          <i className="fa fa-times me-1" aria-hidden="true" />
          Failed
        </Badge>
      </OverlayTrigger>
    );
  }
  if (outcome.deduplicated) {
    return (
      <OverlayTrigger
        overlay={(
          <Tooltip>
            {`Already ingested (${outcome.booking_reference || outcome.booking_id || '—'})`}
          </Tooltip>
        )}
      >
        <Badge bg="secondary">
          <i className="fa fa-check me-1" aria-hidden="true" />
          Already ingested
        </Badge>
      </OverlayTrigger>
    );
  }
  return (
    <OverlayTrigger
      overlay={<Tooltip>{outcome.booking_reference || outcome.booking_id || 'Created'}</Tooltip>}
    >
      <Badge bg="success">
        <i className="fa fa-check me-1" aria-hidden="true" />
        Created
        {outcome.booking_reference ? ` (${outcome.booking_reference})` : ''}
      </Badge>
    </OverlayTrigger>
  );
}

// Per-row schema warnings the agent flagged at proposal time (keys the
// backend's permissive catchall would otherwise swallow). Rendered next to
// the row number so the operator can fix a bad field before "Ingest all".
function WarningBadge({ warnings }) {
  if (!Array.isArray(warnings) || warnings.length === 0) return null;
  return (
    <OverlayTrigger
      overlay={(
        <Tooltip>
          {warnings.map((w) => <div key={w}>{w}</div>)}
        </Tooltip>
      )}
    >
      <Badge bg="warning" text="dark" data-testid="field-warning-badge">
        <i className="fa fa-exclamation-triangle" aria-hidden="true" />
        <span className="visually-hidden">{warnings.length}</span>
      </Badge>
    </OverlayTrigger>
  );
}

// Fetched once on mount; cached for the lifetime of the overlay so
// every row's dropdown shares the same option list. Both Partners and
// VehicleCategories are tiny directories (~tens of entries each), so
// a single GET per overlay open is cheap.
function useDirectoryOptions() {
  const [partners, setPartners] = useState([]);
  const [vehicleCategories, setVehicleCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    PartnersService.fetchPartners()
      .then((resp) => {
        if (cancelled) return;
        const list = resp?.data?.data || [];
        setPartners(list.map((p) => ({ id: p.Id, label: p.name })));
      })
      .catch(() => {
        if (!cancelled) setPartners([]);
      });
    PricingService.fetchVehicleCategories()
      .then((resp) => {
        if (cancelled) return;
        const list = resp?.data?.data || [];
        const active = list
          .filter((c) => c.active === 1 || c.active === true)
          .map((c) => ({
            id: c.Id,
            label: c.name,
            capacity: c.capacity,
            max_luggage: c.max_luggage,
          }));
        setVehicleCategories(active);
      })
      .catch(() => {
        if (!cancelled) setVehicleCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { partners, vehicleCategories };
}

function BookingProposalGrid({
  proposal,
  rows,
  onRowEdit,
  onIngestAll,
  onDiscussChanges,
  hasEdits,
  commitResult,
  fieldWarnings,
  ingesting,
}) {
  const { partners, vehicleCategories } = useDirectoryOptions();

  // "Default partner" picker at the top of the grid — selecting a
  // partner here mass-applies it to every row. Per-row dropdowns can
  // override afterwards. Mirrors the operator's workflow on the
  // booking input form where partner is a top-level choice.
  // Initial value: the most common partner_id across the proposed
  // rows (so if the agent guessed correctly for all rows, the picker
  // pre-selects that partner; if it guessed "__OPERATOR_PICK__" or
  // some invented string, no partner is pre-selected here).
  const [defaultPartner, setDefaultPartner] = useState([]);
  useEffect(() => {
    if (!rows || rows.length === 0 || partners.length === 0) return;
    const allPartnerIds = rows.map((r) => r.partner_id).filter(Boolean);
    if (allPartnerIds.length === 0) return;
    const match = partners.find((p) => p.id === allPartnerIds[0]);
    if (match && allPartnerIds.every((id) => id === allPartnerIds[0])) {
      setDefaultPartner([match]);
    }
    // Deliberately re-run only when the partner directory loads — we
    // don't want operator edits to keep resetting the default picker.
  }, [partners]); // eslint-disable-line

  const handleDefaultPartnerChange = (selected) => {
    setDefaultPartner(selected);
    if (!onRowEdit || !Array.isArray(selected) || selected.length !== 1) return;
    const partnerId = selected[0].id;
    rows.forEach((_, idx) => onRowEdit(idx, { partner_id: partnerId }));
  };

  // Index per-row outcomes by day_index for O(1) lookup during render.
  // Must be declared before any early return — React hooks must run in
  // the same order on every render (see React error #310).
  const outcomesByIdx = useMemo(() => {
    const map = new Map();
    if (commitResult && Array.isArray(commitResult.results)) {
      commitResult.results.forEach((r) => {
        if (typeof r.day_index === 'number') map.set(r.day_index, r);
      });
    }
    return map;
  }, [commitResult]);

  // Per-row schema warnings, keyed by row index. The agent sends them as a
  // plain `{ "0": [...] }` object on the proposal event; normalise lookups to
  // numeric indices to match the row map below.
  const warningsByIdx = useMemo(() => {
    const map = new Map();
    if (fieldWarnings && typeof fieldWarnings === 'object') {
      Object.entries(fieldWarnings).forEach(([idx, msgs]) => {
        if (Array.isArray(msgs) && msgs.length > 0) map.set(Number(idx), msgs);
      });
    }
    return map;
  }, [fieldWarnings]);

  // Empty state — no proposal yet (or empty rows).
  if (!proposal || !Array.isArray(rows) || rows.length === 0) {
    return (
      <div className="card p-4 text-center text-muted h-100" data-testid="proposal-grid-empty">
        <div className="my-auto">
          <i className="fa fa-table fa-2x mb-2 d-block" aria-hidden="true" />
          <p className="mb-2">
            The agent&apos;s proposed bookings will appear here as a
            grid once the offer is drafted.
          </p>
          <p className="mb-0 small">
            Use the chat on the left to revise rows. When you&apos;re
            happy, click
            {' '}
            <strong>Ingest all</strong>
            {' '}
            to write them into the system.
          </p>
        </div>
      </div>
    );
  }

  const { offer_id: offerId } = proposal;
  const summary = commitResult?.summary;

  return (
    <div
      className="card h-100 d-flex flex-column"
      data-testid="proposal-grid"
    >
      <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div>
            <div className="fw-semibold">
              <i className="fa fa-table me-2 text-primary" aria-hidden="true" />
              Proposed bookings
              {' '}
              <Badge bg="secondary" className="ms-1">{rows.length}</Badge>
            </div>
            {offerId && (
              <small className="text-muted font-monospace">
                from offer
                {' '}
                {offerId}
              </small>
            )}
          </div>
          {/* Default-partner picker — mass-applies to every row on change.
              Per-row dropdowns still allow individual overrides. */}
          <div className="d-flex align-items-center gap-2" style={{ minWidth: '320px' }}>
            <small className="text-muted text-nowrap">Default partner</small>
            <div className="flex-grow-1" data-testid="default-partner-picker">
              <Typeahead
                id="ingest-default-partner"
                placeholder="Apply a partner to all rows…"
                options={partners}
                selected={defaultPartner}
                onChange={handleDefaultPartnerChange}
                disabled={ingesting}
                positionFixed
              />
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          {summary && (
            <div className="small text-muted" data-testid="proposal-summary">
              <span className="text-success me-2">
                <i className="fa fa-check-circle me-1" aria-hidden="true" />
                {summary.created || 0}
                {' created'}
              </span>
              {(summary.deduplicated || 0) > 0 && (
                <span className="text-secondary me-2">
                  {summary.deduplicated}
                  {' already ingested'}
                </span>
              )}
              {(summary.failed || 0) > 0 && (
                <span className="text-danger">
                  <i className="fa fa-exclamation-circle me-1" aria-hidden="true" />
                  {summary.failed}
                  {' failed'}
                </span>
              )}
            </div>
          )}
          {onDiscussChanges && (
            <OverlayTrigger
              overlay={(
                <Tooltip>
                  {hasEdits
                    ? 'Send your edits to the agent and ask for feedback'
                    : 'Ask the agent to review the current draft'}
                </Tooltip>
              )}
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={onDiscussChanges}
                disabled={ingesting}
                data-testid="discuss-changes-button"
              >
                <i className="fa fa-comments me-1" aria-hidden="true" />
                Discuss changes
                {hasEdits && (
                  <Badge bg="primary" className="ms-1" data-testid="discuss-edits-badge">
                    edited
                  </Badge>
                )}
              </Button>
            </OverlayTrigger>
          )}
          <Button
            variant="success"
            size="sm"
            onClick={onIngestAll}
            disabled={ingesting || !onIngestAll}
            data-testid="ingest-all-button"
          >
            {ingesting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Ingesting…
              </>
            ) : (
              <>
                <i className="fa fa-cloud-arrow-up me-1" aria-hidden="true" />
                Ingest all
              </>
            )}
          </Button>
        </div>
      </div>
      <div className="table-responsive flex-grow-1" style={{ overflowY: 'auto' }}>
        <Table size="sm" hover className="mb-0 align-middle">
          <thead className="table-light sticky-top">
            <tr>
              <th style={{ width: '40px' }}>#</th>
              <th style={{ minWidth: '180px' }}>Pickup</th>
              <th style={{ minWidth: '180px' }}>Customer</th>
              <th style={{ minWidth: '110px' }}>Pax</th>
              <th>Route</th>
              <th style={{ minWidth: '200px' }}>Vehicle</th>
              <th style={{ minWidth: '220px' }}>Partner</th>
              <th style={{ minWidth: '160px' }} className="text-end">Price</th>
              <th style={{ minWidth: '160px' }}>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const outcome = outcomesByIdx.get(idx);
              const locked = outcome && outcome.ok && outcome.deduplicated;
              return (
                // Rows are intrinsically positional — idx == day_index
                // is baked into the workflow (see
                // `_offer_ingestion_utils.build_idempotency_key`). The
                // agent may re-emit with different row contents but
                // the same indices.
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`proposal-row-${idx}`} data-testid={`proposal-row-${idx}`}>
                  <td className="text-muted small">
                    {idx}
                    {warningsByIdx.has(idx) ? (
                      <span className="ms-1">
                        <WarningBadge warnings={warningsByIdx.get(idx)} />
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      disabled={locked || ingesting}
                      value={isoToBudapestLocalInput(row.date_datetime)}
                      onChange={(e) => {
                        const iso = budapestLocalInputToIso(e.target.value);
                        onRowEdit?.(idx, { date_datetime: iso });
                      }}
                    />
                    <div className="text-muted small font-monospace">
                      {formatPickupDisplay(row.date_datetime)}
                    </div>
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      size="sm"
                      disabled={locked || ingesting}
                      value={row.full_name || ''}
                      onChange={(e) => onRowEdit?.(idx, { full_name: e.target.value })}
                    />
                    {row.phone_number && (
                      <small className="text-muted">{row.phone_number}</small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min={1}
                      disabled={locked || ingesting}
                      style={{ minWidth: '80px' }}
                      value={row.passengers?.adults ?? row.passengers?.amount ?? ''}
                      onChange={(e) => {
                        const adults = parseInt(e.target.value, 10);
                        onRowEdit?.(idx, {
                          passengers: {
                            ...(row.passengers || {}),
                            adults: Number.isFinite(adults) ? adults : undefined,
                            amount: Number.isFinite(adults) ? adults : undefined,
                          },
                        });
                      }}
                    />
                  </td>
                  <td className="small">{formatRoute(row)}</td>
                  <td>
                    <Typeahead
                      id={`vehicle-row-${idx}`}
                      placeholder="Select vehicle…"
                      options={vehicleCategories}
                      selected={
                        row.category?.name
                          ? vehicleCategories.filter((c) => c.label === row.category.name)
                          : []
                      }
                      onChange={(selected) => {
                        if (Array.isArray(selected) && selected.length === 1) {
                          const c = selected[0];
                          onRowEdit?.(idx, {
                            category: {
                              name: c.label,
                              capacity: c.capacity,
                              max_luggage: c.max_luggage,
                            },
                          });
                        } else {
                          onRowEdit?.(idx, { category: undefined });
                        }
                      }}
                      disabled={locked || ingesting}
                      positionFixed
                    />
                    {row.category?.name
                      && !vehicleCategories.some((c) => c.label === row.category.name) && (
                      // Agent invented a vehicle name that isn't in the
                      // directory — surface it so the operator can pick
                      // a real one. The Zod validator on the server side
                      // will reject the original anyway.
                      <small className="text-warning d-block mt-1" title="Not in vehicle catalog">
                        <i className="fa fa-exclamation-triangle me-1" aria-hidden="true" />
                        {row.category.name}
                      </small>
                    )}
                  </td>
                  <td>
                    <Typeahead
                      id={`partner-row-${idx}`}
                      placeholder="Select partner…"
                      options={partners}
                      selected={partners.filter((p) => p.id === row.partner_id)}
                      onChange={(selected) => {
                        if (Array.isArray(selected) && selected.length === 1) {
                          onRowEdit?.(idx, { partner_id: selected[0].id });
                        } else {
                          onRowEdit?.(idx, { partner_id: '' });
                        }
                      }}
                      disabled={locked || ingesting}
                      positionFixed
                    />
                    {row.partner_id && !partners.some((p) => p.id === row.partner_id) && (
                      // Agent supplied a partner_id that isn't in the
                      // partners directory — almost always means it
                      // invented a name like "tourismer" instead of a
                      // real UUID. Surface so the operator picks a
                      // real partner.
                      <small className="text-warning d-block mt-1" title="Not a known partner UUID">
                        <i className="fa fa-exclamation-triangle me-1" aria-hidden="true" />
                        {row.partner_id}
                      </small>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-1 justify-content-end">
                      <Form.Control
                        type="number"
                        size="sm"
                        min={0}
                        disabled={locked || ingesting}
                        value={row.price ?? ''}
                        onChange={(e) => {
                          const price = parseFloat(e.target.value);
                          onRowEdit?.(idx, {
                            price: Number.isFinite(price) ? price : undefined,
                          });
                        }}
                        style={{ width: '90px' }}
                      />
                      <Form.Select
                        size="sm"
                        disabled={locked || ingesting}
                        value={row.currency || 'EUR'}
                        onChange={(e) => onRowEdit?.(idx, { currency: e.target.value })}
                        style={{ width: '70px' }}
                      >
                        <option value="EUR">EUR</option>
                        <option value="HUF">HUF</option>
                      </Form.Select>
                    </div>
                  </td>
                  <td>
                    <ResultBadge outcome={outcome} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

// Exported for testing — pure helpers used by the cell renderers.
export {
  budapestLocalInputToIso,
  isoToBudapestLocalInput,
  formatPickupDisplay,
  formatPassengers,
  formatRoute,
  NUMERIC_FORMATTER,
};

export default BookingProposalGrid;
